<div class="license-plate-container">

    <!-- Input Section -->
    <div class="license-plate-input">
        <h2>{{ 'GUEST_PAY.TITLE' | translate }}</h2>
        <p class="lookup-hint">{{ 'GUEST_PAY.SUBTITLE' | translate }}</p>
        <div class="input-group">
            <app-plate editable>
                <input type="text" [placeholder]="'DASHBOARD.SEARCH_PLACEHOLDER' | translate"
                    class="license-input" [(ngModel)]="licensePlate" (input)="onPlateFieldInput($event)" (keyup.enter)="search()"
                    [disabled]="isLoading()" autocomplete="off" spellcheck="false"
                    [attr.aria-label]="'GUEST_PAY.TITLE' | translate">
            </app-plate>
            <button class="search-btn" (click)="search()" [disabled]="isLoading()">
                @if (isLoading()) {
                <mat-spinner diameter="20"></mat-spinner>
                } @else {
                {{ 'GUEST_PAY.SEARCH_BTN' | translate }}
                }
            </button>
        </div>
    </div>

    <!-- Results Section -->
    @if (hasSearched()) {
    <div class="results-section">

        @if (quote() && isPayable()) {
        <!-- Same card as the activity page's payables: one shape for "you owe this", wherever it is read. -->
        <div class="payable-card">
            <div class="card-header">
                @let plate = getPlatePresentation(searchedPlate());
                <app-plate [number]="plate.fullNumber" [euBand]="plate.euBand"
                    [countryCode]="plate.countryCode" [region]="plate.region"
                    [regionSeparator]="plate.regionSeparator" />
                <span class="status-chip" [class.active]="isActive()" [class.ended]="!isActive()">
                    {{ (isActive() ? 'ACTIVITY.ACTIVE' : 'ACTIVITY.COMPLETED') | translate }}
                </span>
            </div>

            <div class="card-body">
                <div class="info-grid">
                    <div class="info-item">
                        <span class="label">{{ 'ACTIVITY.STARTED_AT' | translate }}</span>
                        <span class="value">{{ quote()!.startedAt | date:'medium' }}</span>
                    </div>
                    @if (quote()!.endedAt) {
                    <div class="info-item">
                        <span class="label">{{ 'ACTIVITY.ENDED_AT' | translate }}</span>
                        <span class="value">{{ quote()!.endedAt | date:'medium' }}</span>
                    </div>
                    }
                    <div class="info-item">
                        <span class="label">{{ 'ACTIVITY.DURATION' | translate }}</span>
                        <span class="value duration-value">
                            {{ getDuration(quote()!.startedAt, quote()!.endedAt) }}
                        </span>
                    </div>
                    @if (quote()!.parkingZoneName) {
                    <div class="info-item">
                        <span class="label">{{ 'ACTIVITY.ZONE' | translate }}</span>
                        <span class="value">{{ quote()!.parkingZoneName }}</span>
                    </div>
                    }
                    @if (quote()!.parkingSpaceName) {
                    <div class="info-item">
                        <span class="label">{{ 'ACTIVITY.SPACE' | translate }}</span>
                        <span class="value">{{ quote()!.parkingSpaceName }}</span>
                    </div>
                    }
                </div>

                <div class="price-breakdown">
                    @if (quote()!.grossAmount && quote()!.grossAmount! > amountDue()) {
                    <div class="price-row muted">
                        <span>{{ 'ACTIVITY.GROSS_AMOUNT' | translate }}</span>
                        <span>{{ quote()!.grossAmount | currency:(quote()!.currency || 'EUR'):'symbol':'1.2-2' }}</span>
                    </div>
                    }
                    @if (quote()!.netPaidAmount && quote()!.netPaidAmount! > 0) {
                    <div class="price-row muted">
                        <span>{{ 'ACTIVITY.PAID_AMOUNT' | translate }}</span>
                        <span>-{{ quote()!.netPaidAmount | currency:(quote()!.currency || 'EUR'):'symbol':'1.2-2' }}</span>
                    </div>
                    }
                    <div class="price-row total">
                        <span>{{ 'ACTIVITY.DUE_AMOUNT' | translate }}</span>
                        <span class="due-amount">
                            {{ amountDue() | currency:(quote()!.currency || 'EUR'):'symbol':'1.2-2' }}
                        </span>
                    </div>
                </div>

                @if (isFrozen()) {
                <p class="frozen-note">
                    <mat-icon>lock</mat-icon>
                    {{ 'GUEST_PAY.FROZEN_NOTE' | translate }}
                </p>
                }
            </div>

            <div class="card-footer">
                <jjames-button variant="primary" matIconLeft="payment" (clicked)="pay()">
                    {{ 'ACTIVITY.PAY_BUTTON' | translate }}
                    ({{ amountDue() | currency:(quote()!.currency || 'EUR'):'symbol':'1.2-2' }})
                </jjames-button>
            </div>

        </div>
        }

        @else if (quote()) {
        <!-- Found the stay, nothing to collect. Each reason gets its own words. -->
        <div class="settled-card">
            <div class="settled-icon"><mat-icon>check_circle</mat-icon></div>
            <h3>{{ nothingDueKey() + '_TITLE' | translate }}</h3>
            <p>{{ nothingDueKey() + '_TEXT' | translate }}</p>
            <p class="settled-times">
                {{ 'ACTIVITY.STARTED_AT' | translate }}: {{ quote()!.startedAt | date:'medium' }}
                @if (quote()!.endedAt) {
                <br />{{ 'ACTIVITY.ENDED_AT' | translate }}: {{ quote()!.endedAt | date:'medium' }}
                }
                @if (quote()!.parkingZoneName) {
                <br />{{ 'ACTIVITY.ZONE' | translate }}: {{ quote()!.parkingZoneName }}
                }
                @if (quote()!.parkingSpaceName) {
                <br />{{ 'ACTIVITY.SPACE' | translate }}: {{ quote()!.parkingSpaceName }}
                }
            </p>
            @if (graceUntil()) {
            <p class="grace-until">
                {{ 'GUEST_PAY.GRACE_UNTIL' | translate }}
                <strong>{{ graceUntil() | date:'shortTime' }}</strong>
            </p>
            }

            @if (isPaid()) {
            <div class="receipts">
                <p class="receipt-note">{{ 'GUEST_PAY.RECEIPT_ONCE' | translate }}</p>
                @if (receipts(); as list) {
                @if (list.length === 0) {
                <p class="no-receipts">{{ 'GUEST_PAY.NO_RECEIPTS' | translate }}</p>
                } @else {
                <ul class="receipt-list">
                    @for (r of list; track r.receiptId) {
                    <li class="receipt-row">
                        <div class="receipt-meta">
                            <span class="receipt-date">{{ r.paidAt | date:'medium' }}</span>
                            <span class="receipt-amount">{{ r.amount | currency:(r.currency || 'EUR'):'symbol':'1.2-2' }}</span>
                        </div>
                        <div class="receipt-actions">
                            @if (r.receiptUrl) {
                            <a class="receipt-link" [href]="r.receiptUrl" target="_blank" rel="noopener noreferrer"
                                (click)="markDownloaded(r)">
                                <mat-icon>download</mat-icon>
                                {{ 'GUEST_PAY.OPEN_RECEIPT' | translate }}
                            </a>
                            }
                        </div>
                    </li>
                    }
                </ul>
                }
                } @else {
                <jjames-button variant="secondary" matIconLeft="receipt_long" [loading]="receiptsLoading()"
                    (clicked)="loadReceipts()">
                    {{ 'GUEST_PAY.GET_RECEIPTS' | translate }}
                </jjames-button>
                }
            </div>
            }

            <button class="clear-btn" (click)="clear()">{{ 'GUEST_PAY.SEARCH_AGAIN' | translate }}</button>
        </div>
        }

        @else {
        <div class="no-results">
            <div class="no-results-icon">🚗</div>
            <p>{{ 'GUEST_PAY.NOT_FOUND' | translate }}</p>
            <button class="clear-btn" (click)="clear()">{{ 'GUEST_PAY.SEARCH_AGAIN' | translate }}</button>
        </div>
        }

    </div>
    }

</div>
